"use client";

import Image from "next/image";
import { APP_BASE_URL } from "@/config/apiConfig";
import { Suspense, useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import imgUp from "@/public/ico_up.png";
import { useAuth } from "@/hooks/useAuth";
import obituaryService from "@/services/obituary-service";
import Layout from "../../components/appcomponents/Layout";
import { getTemplateCardImages } from "@/utils/commonUtils";
import Breadcrumbs from "../../components/appcomponents/Breadcrumbs";
import ModalLibrary from "../../components/appcomponents/ModalLibrary";
import ImageFullView from "../../components/appcomponents/ImageFullView";
import ObituaryPublished from "../../components/appcomponents/ObituaryPublished";
import AnnouncementBlock from "../../components/appcomponents/AnnouncementBlock";
import MemorialPageTopComp from "../../components/appcomponents/MemorialPageTopComp";
import FlowerShops, {
  FlowerShops2,
} from "../../components/appcomponents/FlowerShops";
import ShippingNotifications from "../../components/appcomponents/ShippingNotifications";
import PrivateMemoryGate from "../../components/appcomponents/PrivateMemoryGate";
import OrderFlow from "../orders/OrderFlow";

/** Digicards: hide for everyone 7 days after obituary page creation (DB `createdTimestamp`). */
const MEMORY_CARDS_VISIBLE_AFTER_PAGE_CREATION_DAYS = 7;
const MEMORY_CARDS_VISIBILITY_MS =
  MEMORY_CARDS_VISIBLE_AFTER_PAGE_CREATION_DAYS * 24 * 60 * 60 * 1000;

const shouldShowMemoryCardTemplates = (obituary) => {
  const ts = obituary?.createdTimestamp;
  if (!ts) return true;
  const createdMs = new Date(ts).getTime();
  if (Number.isNaN(createdMs)) return true;
  return Date.now() <= createdMs + MEMORY_CARDS_VISIBILITY_MS;
};

const MemoryPageContent = ({ params, obituaryDataFromServer }) => {
  const { slugKey } = params;
  const router = useRouter();
  const searchParams = useSearchParams();

  const { user } = useAuth();
  const currentSlugRef = useRef(null);

  const [isShowModal, setIsShowModal] = useState(false);
  const [select_id, setSelect_Id] = useState("");
  // The paid popups (Zahvala, Spomin, premium candle) open on top of the page, already for this memory page.
  const [orderProduct, setOrderProduct] = useState(null);
  const openOrder = (product) => {
    setIsShowModal(false);
    setOrderProduct(product);
  };
  const [selectedImage, setSelectedImage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [showShops, setShowShops] = useState(false);
  const [showImageView, setShowImageView] = useState(false);
  const [imageId, setImageId] = useState("0");
  const [obituary, setObituary] = useState(obituaryDataFromServer || {});
  // What the server gave when the code of a private page was entered. It lives only in this open page (not in the
  // browser's storage): the next visit asks for the code again.
  const [accessToken, setAccessToken] = useState(null);
  const city = searchParams.get("city");
  const region = searchParams.get("region");

  const isKeeperActive = (k) =>
    !k?.expiry || new Date(k.expiry).getTime() > Date.now();

  const isKeeper = Boolean(
    (user?.id &&
      obituary?.Keepers?.some(
        (k) => (k.userId === user.id || k.user_id === user.id) && isKeeperActive(k)
      )) ||
      (currentUser?.id &&
        obituary?.Keepers?.some(
          (k) =>
            (k.userId === currentUser.id || k.user_id === currentUser.id) &&
            isKeeperActive(k)
        ))
  );

  const hasActiveKeeper = Boolean(
    obituary?.Keepers?.some((k) => isKeeperActive(k))
  );

  // A private page arrives from the server as just { id, slugKey, isPrivate, locked } until the code is entered.
  const isLocked = Boolean(obituary?.locked || (obituary?.isPrivate && !isKeeper && !accessToken));

  // The code was right: fetch the whole page with the access the server gave.
  const unlockWith = async (token) => {
    try {
      const response = await obituaryService.getMemory({ slugKey }, token);

      if (!response?.obituary || response.obituary.locked) {
        throw new Error("Unlock failed");
      }

      setObituary(response.obituary);
      setAccessToken(token);
    } catch (error) {
      setAccessToken(null);
      console.error("Error unlocking memory page:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (!slugKey) return;

    currentSlugRef.current = slugKey;
    let cancelled = false;

    const fetchMemory = async () => {
      try {
        const response = await obituaryService.getMemory({ slugKey });

        if (cancelled || currentSlugRef.current !== slugKey) return;
        if (response.error) return;

        setObituary(response.obituary);

        if (response?.obituary && !response.obituary.locked) {
          let userId = currentUser?.id ?? null;

          if (userId == null) {
            try {
              const stored = localStorage.getItem("user");
              if (stored) {
                const parsed = JSON.parse(stored);
                userId = parsed?.id ?? null;
              }
            } catch (_) {}
          }

          await obituaryService.updateObituaryVisits({
            obituaryId: response.obituary.id,
            userId,
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching obituary:", err);
        }
      }
    };

    fetchMemory();

    return () => {
      cancelled = true;
    };
  }, [slugKey]);
  
  // Same data the page loads with, fetched again without reloading the page (used when the candle popup closes).
  const refreshMemory = async () => {
    if (!slugKey) return;
    try {
      const response = await obituaryService.getMemory({ slugKey }, accessToken);
      if (response?.obituary && !response.obituary.locked) setObituary((prev) => ({ ...prev, ...response.obituary }));
    } catch (err) {
      console.error("Error refreshing obituary:", err);
    }
  };

  const updateObituary = (updatedData) => {
    setObituary((prevObituary) => ({
      ...prevObituary,
      ...updatedData,
    }));
  };

  useEffect(() => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) return;

  try {
    setCurrentUser(JSON.parse(storedUser));
  } catch (_) {
    setCurrentUser(null);
  }
}, []);

  const handleMemoryChange = async (type) => {
    try {
      // Use city and region from searchParams if available, fallback to obituary
      const queryParams = {
        city: city || obituary.city,
        region: region || obituary.region,
        date: obituary.createdTimestamp,
        type: type,
      };

      const response = await obituaryService.getMemoryId(queryParams);

      const data = response;

      const urlParams = [];
      if (queryParams.city)
        urlParams.push(`city=${encodeURIComponent(queryParams.city)}`);
      if (queryParams.region)
        urlParams.push(`region=${encodeURIComponent(queryParams.region)}`);
      const queryString = urlParams.length ? `?${urlParams.join("&")}` : "";

      // Temporarily commented
      router.push(`/m/${data.slugKey}${queryString}`);
    } catch (error) {
      console.error("Error fetching memory:", error);
      if (error?.response?.status === 404) {
        // toast.error(`No ${type} memory exists`);
      } else {
        // toast.error("Prišlo je do napake.");
      }
    }
  };

  const fullName =
    obituary?.name && obituary?.sirName ?
      `${obituary.name} ${obituary.sirName}`
    : obituary?.name || "";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Domov",
        item: `${APP_BASE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Osmrtnice",
        item: `${APP_BASE_URL}/osmrtnice`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: fullName || "Žalna stran",
        item: `${APP_BASE_URL}/m/${slugKey}`,
      },
    ],
  };

  // Convert and Upload image
  const handleFacebookShare = async () => {
    try {
      const pageUrl = `${APP_BASE_URL}/m/${slugKey}`;
      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        pageUrl,
      )}`;
      const width = 600;
      const height = 500;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(
        shareUrl,
        "facebook-share-dialog",
        `width=${width},height=${height},top=${top},left=${left}`,
      );
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  return (
    <>
      <Layout
        from={"3"}
        onChangeMemory={handleMemoryChange}
        forFooter={"memorypage"}
      >
        <div className="flex flex-1 flex-col mx-auto bg-[#ecf0f3] pt-[20px] max-w-[100vw] overflow-x-hidden">
          <Breadcrumbs
            items={[
              { label: "Domov", href: "/" },
              { label: "Osmrtnice", href: "/osmrtnice" },
              { label: fullName || "Žalna stran" },
            ]}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbJsonLd),
            }}
          />
          {orderProduct && (
            <OrderFlow product={orderProduct} obituary={obituary} onClose={() => setOrderProduct(null)} />
          )}
          <ModalLibrary
            onCandlePopupClosed={refreshMemory}
            onOrder={openOrder}
            isShowModal={isShowModal}
            setIsShowModal={setIsShowModal}
            select_id={select_id}
            set_Id={setSelect_Id}
            selectedImage={selectedImage}
            data={obituary}
            updateObituary={updateObituary}
          />
          {isLocked ? (
            <PrivateMemoryGate
              obituary={obituary}
              onUnlocked={unlockWith}
            />
          ) : (
            <>
              <ImageFullView
                showImageView={showImageView}
                imageId={imageId}
                setShowImageView={setShowImageView}
                data={obituary?.Photos}
              />
              <MemorialPageTopComp
                onOrder={openOrder}
                set_Id={setSelect_Id}
                setModal={setIsShowModal}
                data={obituary}
                updateObituary={updateObituary}
                handleFacebookShare={handleFacebookShare}
              />

              {!hasActiveKeeper && (
                <AnnouncementBlock
                  deceased={{
                    name: obituary.name,
                    sirName: obituary.sirName,
                    id: obituary.id,
                    city: obituary.city,
                  }}
                />
              )}

              {/* The funeral cards are about a funeral: a memory page only (no obituary) never shows them. */}
              {!obituary?.skipObituaryBox && shouldShowMemoryCardTemplates(obituary) && (
                <ShippingNotifications
                  set_Id={setSelect_Id}
                  setModal={setIsShowModal}
                  images={getTemplateCardImages(obituary?.cardImages)}
                  blurredImages={Boolean(obituary?.cardImages?.length)}
                  slugKey={obituary?.slugKey}
                  deceasedName={[obituary?.name, obituary?.sirName].filter(Boolean).join(" ")}
                />
              )}

              <FlowerShops
                setIsOpen={(value) => {
                  setShowShops(value);
                }}
                data={obituary}
                showShop={showShops}
              />

              {/* <FlowerShops2
                setIsOpen={(value) => {
                  setShowShops(value);
                }}
                showShop={showShops}
              /> */}

              <ObituaryPublished
                set_Id={setSelect_Id}
                setModal={setIsShowModal}
                data={obituary}
              />
              <a
                className="z-50 bottom-10 right-10 fixed w-[48px] h-[48px] mt-[26px] 
                    shadow-custom-light-dark bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF]
                    flex justify-center items-center rounded-lg"
                href="#memoryPageTop"
              >
                <Image
                  src={imgUp}
                  alt="imgPrevious"
                  className=" w-[24px] h-[24px]"
                />
              </a>
            </>
          )}
        </div>
      </Layout>
    </>
  );
};

const MemoryPageClientComponent = (props) => (
  <Suspense fallback={<div>Loading...</div>}>
    <MemoryPageContent {...props} />
  </Suspense>
);

export default MemoryPageClientComponent;
